import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  Paper,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Message as MessageIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  AutoFixHigh as AutoFixHighIcon,
  ChevronLeft,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import ConversationThreadMessages from './ConversationThreadMessages';
import { api } from '../services/authService';

// Helper function to get initials from name
const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case "open":
      return "success";
    case "assigned":
      return "primary";
    case "closed":
      return "default";
    default:
      return "default";
  }
};

// Helper function to get channel icon
const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case "sms":
      return <MessageIcon />;
    case "email":
      return <MailIcon />;
    case "voice":
      return <PhoneIcon />;
    case "chat":
      return <ChatIcon />;
    default:
      return <MessageIcon />;
  }
};

const ConversationThreadPanel = ({ 
  selectedConversation, 
  onBack, 
  onSendMessage,
  onAssign,
  onMarkResolved,
  onSnooze,
  onViewContact,
  onAddNote,
  onAddToCampaign,
  onDeleteConversation,
  showDetails = false,
  threads = [],
  onThreadSelect,
  messages = [],
  selectedThread,
  onThreadCreated
}) => {
  const [replyText, setReplyText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [channelAnchorEl, setChannelAnchorEl] = useState(null);
  const [createThreadDialogOpen, setCreateThreadDialogOpen] = useState(false);
  const [newThreadData, setNewThreadData] = useState({
    subject: '',
    channel: 'sms',
    status: 'open',
  });
  const [isCreatingThread, setIsCreatingThread] = useState(false);

  const getFullName = () => {
    const firstName = selectedConversation?.first_name || '';
    const lastName = selectedConversation?.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Lead';
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChannelClick = (event) => {
    setChannelAnchorEl(event.currentTarget);
  };

  const handleChannelClose = () => {
    setChannelAnchorEl(null);
  };

  const handleSendReply = () => {
    if (replyText.trim()) {
      onSendMessage(replyText);
      setReplyText("");
    }
  };

  const handleCreateThread = async () => {
    if (!selectedConversation) return;
    
    setIsCreatingThread(true);
    try {
      const response = await api.post('/communications/conversation-threads/', {
        lead_id: selectedConversation.id,
        subject: newThreadData.subject,
        channel: newThreadData.channel,
        status: newThreadData.status,
      });

      // Call the callback to refresh threads
      if (onThreadCreated) {
        onThreadCreated();
      }

      setCreateThreadDialogOpen(false);
      setNewThreadData({
        subject: '',
        channel: 'sms',
        status: 'open',
      });
    } catch (error) {
      // Handle error
    } finally {
      setIsCreatingThread(false);
    }
  };

  const handleNewThreadClick = () => {
    setCreateThreadDialogOpen(true);
  };

  if (!selectedConversation) {
    return (
      <Card sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent>
          <Typography variant="h6" align="center">
            No conversation selected
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Select a conversation from the list to view details
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (showDetails) {
    return (
      <ConversationThreadMessages
        selectedThread={selectedThread}
        messages={messages}
        onBack={onBack}
        onSendMessage={onSendMessage}
        onAssign={onAssign}
        onMarkResolved={onMarkResolved}
        onSnooze={onSnooze}
        onViewContact={onViewContact}
        onAddNote={onAddNote}
        onAddToCampaign={onAddToCampaign}
        onDeleteConversation={onDeleteConversation}
      />
    );
  }

  return (
    <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar>{getInitials(getFullName())}</Avatar>
            <Box>
              <Typography variant="h6">{getFullName()}</Typography>
              <Typography variant="body2" color="text.secondary">
                {threads.length} total threads with this contact
              </Typography>
            </Box>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleNewThreadClick}
          >
            New Conversation
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Conversation Threads
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedConversation?.active_threads_count} active threads
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {threads.map((thread) => (
          <Box
            key={thread.id}
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
              bgcolor: selectedThread === thread.id ? 'action.selected' : 'transparent',
            }}
            onClick={() => {
              if (thread.id) {
                onThreadSelect(thread.id);
              } else {
                console.error('Invalid thread selected:', thread);
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">{thread.subject || 'No subject'}</Typography>
              <Chip
                label={thread.status || 'unknown'}
                color={
                  thread.status === 'open'
                    ? 'success'
                    : thread.status === 'assigned'
                    ? 'primary'
                    : 'default'
                }
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                icon={<ChannelIcon channel={thread.channel} />}
                label={thread.channel || 'unknown'}
                variant="outlined"
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {thread.last_message_timestamp || thread.updated_at ? new Date(thread.last_message_timestamp || thread.updated_at).toLocaleString() : 'No timestamp'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {thread.messages.length || 0} messages
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  View conversation
                </Typography>
                <ArrowForwardIcon fontSize="small" />
              </Box>
            </Box>
          </Box>
        ))}

        {threads.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No conversation threads found for this contact
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create Thread Dialog */}
      <Dialog 
        open={createThreadDialogOpen} 
        onClose={() => !isCreatingThread && setCreateThreadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Thread</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Subject"
              value={newThreadData.subject}
              onChange={(e) => setNewThreadData({ ...newThreadData, subject: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={newThreadData.channel}
                label="Channel"
                onChange={(e) => setNewThreadData({ ...newThreadData, channel: e.target.value })}
              >
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="voice">Voice</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newThreadData.status}
                label="Status"
                onChange={(e) => setNewThreadData({ ...newThreadData, status: e.target.value })}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCreateThreadDialogOpen(false)} 
            disabled={isCreatingThread}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateThread} 
            variant="contained" 
            disabled={isCreatingThread || !newThreadData.subject.trim()}
            startIcon={isCreatingThread ? <CircularProgress size={20} /> : null}
          >
            Create Thread
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ConversationThreadPanel; 